/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChatRoom } from "../models/ChatRoom";
import { MultiUserRoom} from "../models/MultiUserRoom";
import { Message } from "../models/Message";
import { FileMessage } from "../models/FileMessage";
import { SeenEntry } from "../models/ChatMessage";
import { messageKey } from "../utils/messageKey";
import { secureGet, secureSet } from "../utils/secureStorage";

class ChatController {
  chatRooms: ChatRoom[] = [];
  activeChatRoomId?: string;

  constructor() {
    this.loadChatRooms();
  }

  public saveChatRooms() {

    if (typeof window !== "undefined") {
      secureSet(localStorage,
        "chatRooms",
        JSON.stringify(
          this.chatRooms.map((room) => ({
            chatRoomId: room.chatRoomId,
            username: room.username,
            messages: room.messages.map((msg) => (msg as Message||FileMessage).toMap()), 
            type: room.type,
            isSessionActive: room.isSessionActive,
            unreadMessageCount: room.unreadMessageCount,
            isConference: room.isConference,
            isGroup: room.isGroup,
            isAgent: room.isAgent,
            isCreator: room.isCreator,
            admin: room.admin,
            participants: room instanceof MultiUserRoom ? room.participants : [],
          }))
        )
      );
    }
  }

  public loadChatRooms() {
    if (typeof window !== "undefined") {
      const storedChatRooms = secureGet(localStorage, "chatRooms");
      if (storedChatRooms) {
        this.chatRooms = JSON.parse(storedChatRooms).map((roomData: any) => {
          const messages = roomData.messages.map((msgData: any) => {
            // Parse seenTimes properly
            let seenTimes: SeenEntry[] = [];
            if (msgData.seenTimes !== undefined && msgData.seenTimes !== null) {
              if (typeof msgData.seenTimes === 'string') {
                try {
                  if (msgData.seenTimes === '' || msgData.seenTimes === '[]') {
                    seenTimes = [];
                  } else {
                    const parsedSeenTimes = JSON.parse(msgData.seenTimes);
                    seenTimes = Array.isArray(parsedSeenTimes) ? parsedSeenTimes.map((entry: any) => ({
                      name: entry.name,
                      time: new Date(entry.time)
                    })) : [];
                  }
                } catch (error) {
                  console.warn("Failed to parse seenTimes string:", msgData.seenTimes, error);
                  seenTimes = [];
                }
              } else if (Array.isArray(msgData.seenTimes)) {
                seenTimes = msgData.seenTimes.map((entry: any) => ({
                  name: entry.name,
                  time: new Date(entry.time)
                }));
              }
            }

            // Check if it's a FileMessage
            if (msgData.fileName && msgData.filePath && msgData.fileType) {
              return new FileMessage(
                msgData.text || msgData.message,
                msgData.fileName,
                msgData.filePath,
                msgData.fileType,
                msgData.messageId,
                msgData.isOwner || false,
                msgData.chatRoomId || '',
                msgData.senderName,
                msgData.repliedTo || null,
                msgData.sentTime ? msgData.sentTime : null,
                msgData.deliveredTime ? msgData.deliveredTime : null,
                msgData.arrivalTime ? msgData.arrivalTime : null,
                seenTimes,
                msgData.editedTime ? (msgData.editedTime) : null,
                msgData.deletedTime ? msgData.deletedTime : null,
              );
            } else {
              // Create Message with new constructor parameters
              // Parameter order must match Message constructor:
              // (text, messageId, isOwner, chatRoomId, senderName, repliedTo,
              //  sentTime, deliveredTime, seenTimes, editedTime, deletedTime,
              //  arrivalTime, messageType?, fileData?, pageNumber?, totalPageNumber?)
              return new Message(
                msgData.text || msgData.message,
                msgData.messageId,
                msgData.isOwner || false,
                msgData.chatRoomId,
                msgData.senderName,
                msgData.repliedTo || null,
                msgData.sentTime ? new Date(msgData.sentTime) : null,
                msgData.deliveredTime ? new Date(msgData.deliveredTime) : null,
                seenTimes,
                msgData.editedTime ? new Date(msgData.editedTime) : null,
                msgData.deletedTime ? new Date(msgData.deletedTime) : null,
                msgData.arrivalTime && msgData.arrivalTime !== "text" ? new Date(msgData.arrivalTime) : null,
                msgData.messageType || "text",
                msgData.fileData || undefined,
                msgData.pageNumber,
                msgData.totalPageNumber
              );
            }
          });
  
          const room = new ChatRoom(
            roomData.chatRoomId,
            roomData.username,
            messages,
            roomData.type,
            roomData.isSessionActive,
            roomData.unreadMessageCount,
            roomData.isConference,
            roomData.isGroup,
            roomData.isAgent,
            roomData.isCreator,
            roomData.admin,
            roomData.participants || []
          );
          console.log(`[UNREAD] loadChatRooms - room "${roomData.username}" loaded with unreadCount=${roomData.unreadMessageCount}`);
          return room;
        });
        console.log("[UNREAD] loadChatRooms - done, counts:", this.chatRooms.map(r => `${r.username}:${r.unreadMessageCount}`).join(", "));
      }
    }
  }
  

  getMessagesForChatRoom(chatRoomId: string): (Message | FileMessage)[] {
    const room = this.chatRooms.find((room) => room.chatRoomId === chatRoomId);
    return room ? (room.messages as (Message | FileMessage)[]) : [];
  }

  getRoomById(roomId: string): ChatRoom | undefined {
    return this.chatRooms.find(room => room.chatRoomId === roomId);
  }
  
  getRooms(): ChatRoom[] | undefined {
    return this.chatRooms;
  }

  createChatRoom(
    chatroomId: string,
    chatRoomName: string,
    isAgent: boolean,
    status?: string
  ): ChatRoom {
    let existingChatRoom = this.chatRooms.find(
      (room) => room.chatRoomId === chatroomId && room.username === chatRoomName
    );
    let isactive = status === "active" ? true : false;
    if (existingChatRoom) {
      if (status){
        existingChatRoom.isSessionActive = isactive;
      }else{
        existingChatRoom.isSessionActive = true;
      }
      existingChatRoom.isAgent = isAgent;
      this.saveChatRooms();
      return existingChatRoom;
    }else{
      let type;
      if(isAgent){
         type="agent-to-agent";
      }else{
        type="c2a";
      }
      const newChatRoom = new ChatRoom(
        chatroomId,
        chatRoomName,
        [],
        type
      );
      newChatRoom.isAgent = isAgent;
      newChatRoom.isSessionActive = isactive;
      this.chatRooms.push(newChatRoom);
      this.saveChatRooms();
      return newChatRoom;
    }
  }

  createMultiUserRoom(
    chatRoomId: string,
    roomName: string,
    messages: (Message | FileMessage)[],
    status: string,
    participants: string[],
    type: string,
    iscreator: boolean | null,
    admin: string | null
  ): MultiUserRoom{
    console.log("Creating multi-user room with ID:", chatRoomId,"roomName:", roomName, "status:", status, "participants:", participants, "type:", type, "isCreator:", iscreator,"admin",admin);

    let isactive;
    if (status === "active") {
      isactive = true;
      console.log("Setting isactive to true for room:", chatRoomId);
    }else{
      isactive = false;
      console.log("Setting isactive to false for room:", chatRoomId);
    }
    const existingRoom = this.chatRooms.find(
      (room) => room.chatRoomId === chatRoomId &&room instanceof MultiUserRoom
    ) as MultiUserRoom;

    if (existingRoom) {
      existingRoom.participants = participants;
      existingRoom.isSessionActive = isactive;
      existingRoom.isCreator = iscreator;
      existingRoom.admin=admin;
      existingRoom.type = type;
      return existingRoom;
    }else{
      const newRoom = new MultiUserRoom({
        chatRoomId,
        roomName,
        messages,
        type,
        isSessionActive: isactive,
        unreadMessageCount: 0,
        participants,
        isCreator: iscreator,
        admin: admin
      });

      if(type==="group-chat"){
        newRoom.isGroup = true;
      }else if(type==="conference"){
        newRoom.isConference = true;
      }
      
      this.chatRooms.push(newRoom);
      return newRoom;
    }
  }

  addParticipant(chatRoomName: string, participant: string): void {
    const room = this.chatRooms.find(
      (room) => room.username === chatRoomName && room instanceof MultiUserRoom
    ) as MultiUserRoom| undefined;
    if (room) room.addParticipant(participant);
  }

  removeParticipant(chatRoomId: string, participant: string): void {
    const room = this.chatRooms.find(
      (room) => room.chatRoomId === chatRoomId && room instanceof MultiUserRoom
    ) as MultiUserRoom| undefined;
    if (room) room.removeParticipant(participant);
  }

addMessage(message: Message | FileMessage, chatRoomId: string) {
  const room = this.chatRooms.find((r) => r.chatRoomId === chatRoomId);
  if (!room) return undefined;
console.log("Adding single message to room:", chatRoomId, "message ID:", (message as any).messageId," and message:", message);
  const incomingKey = messageKey((message as any).messageId);
  const exists = room.messages.some((m: any) => messageKey(m.messageId) === incomingKey);
  if (exists) return room;

  const pageNumber = (message as any)?.pageNumber ?? 1;
  console.log("Adding message to room:", chatRoomId, "Page number:", pageNumber, "message:", message.text);
  if (pageNumber > 1) {
    room.messages.unshift(message);
  } else {
    room.messages.push(message);
  }

  this.saveChatRooms();
  return room;
}
addMessages(messages: (Message | FileMessage)[], chatRoomId: string, position: "top" | "bottom" = "bottom") {
console.log("Adding messages to room:", chatRoomId, "Position:", position, "Messages count:", messages.length);
  const room = this.chatRooms.find((r) => r.chatRoomId === chatRoomId);
  if (!room) return undefined;
  if (!messages || messages.length === 0) return room;

  // Build a set of existing ids for fast dedupe
  const existingIds = new Set(room.messages.map((m: any) => messageKey(m.messageId)));
  const unique = messages.filter((m: any) => m && !existingIds.has(messageKey(m.messageId)));

  if (unique.length === 0) return room;

  if (position === "top") {
    // Prepend the whole block as-is (keep incoming order)
    room.messages = [...unique, ...room.messages];
  } else {
    // Append as-is
    room.messages.push(...unique);
  }

  this.saveChatRooms();
  return room;
}


  removeMessage(messageId: string, chatRoomId: string) {
    const room = this.chatRooms.find((room) => room.chatRoomId === chatRoomId);
    if (room) {
      const message = room.messages.find((msg) => msg.messageId === messageId);
        room.messages = room.messages.filter((msg) => msg.messageId !== message?.messageId);
        this.saveChatRooms(); 
    }
    return room;
  }

  markMessagesAsRead(chatRoomId: string) {
    const room = this.chatRooms.find((room) => room.chatRoomId === chatRoomId);
    if (room) {
      room.unreadMessageCount = 0;
    }
  }

  deleteChatRoom(chatRoomId: string) {
    const room = this.chatRooms.find((room) => room.chatRoomId === chatRoomId);
    if (room) {
        room.isSessionActive = false;
    }
    this.chatRooms = this.chatRooms.filter(
      (room) => room.chatRoomId !== chatRoomId
    );
    
    this.saveChatRooms(); 
  }
 
  findChatRoomIdByMessageId(messageId: string): string | undefined {
    for (const room of this.chatRooms) {
      if (room.messages.some((msg) => msg.messageId === messageId)) {
        return room.chatRoomId;
      }
    }
    return undefined; 
  }

  setChatroomSessionStatus(
    chatRoomName: string,
    status: boolean
  ): ChatRoom | undefined {
    const room = this.chatRooms.find((room) => room.username === chatRoomName);
    if (room) {
      room.isSessionActive = status;
      this.saveChatRooms();
    }
    return room;
  }
}

export default ChatController;